import dayjs from 'dayjs';

export const SCRIPT_NAME = 'workbench';
export const SCRIPT_EVENTS = 'events';
export const SCRIPT_NEXT_STAGE = 'next-stage';
export const SCRIPT_EXIT = 'exit';
export const SCRIPT_CLAIM = 'claim';
export const DEFAULT_PROCESS_CODE = '10316';
export const QUERY_V3_USERS = 'getUsers';
export const REASSIGN_REASONS = ['Management Directive', 'Vacation/Leave', 'Conflict'];
export const WORKBENCH_EVIDENCE_TYPE = {
	LINKED_ATTACHMENT: 'LINKED_ATTACHMENT',
	CHECKLIST: 'CHECKLIST',
	LINKED_SOLUTION: 'LINKED_SOLUTION',
	EVIDENCE_NOTE: 'EVIDENCE_NOTE',
};

export const getResolvedStaticState = ({ locationState, encodedState }) => {
	const safeLocationState = locationState && typeof locationState === 'object' ? locationState : null;
	const safeEncodedState = encodedState && typeof encodedState === 'object' ? encodedState : null;

	return {
		...(safeEncodedState ?? {}),
		...(safeLocationState ?? {}),
	};
};

export const toTrimmedString = (value) => (value ?? '').toString().trim();
const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const getFirstNonEmptyObject = (candidates = []) => candidates.find((candidate) => isPlainObject(candidate) && Object.keys(candidate).length) ?? {};

const getResourceId = (row) => toTrimmedString(row?._id ?? row?.id);

const getStageKey = (row) => toTrimmedString(row?.currentStage?.stageKey);
const getStageName = (row) => toTrimmedString(row?.currentStage?.stageName);

const getEnteredAt = (row) => {
	const enteredAt = row?.currentStage?.enteredAt;
	return enteredAt?.localDateTime ?? enteredAt?.localDateTimeString ?? enteredAt?.dateString ?? null;
};

const getCreatedAt = (row) => {
	const createdAt = row?.createdAt;
	return createdAt?.localDateTime ?? createdAt?.localDateTimeString ?? createdAt?.dateString ?? null;
};

const getSlaDate = (value) => {
	const at = value && typeof value === 'object' ? value : null;
	if (!at) return null;
	return at?.localDateTime ?? at?.localDateTimeString ?? at?.dateString ?? null;
};

const getTaskDateTime = (value) =>
	toTrimmedString(value?.localDateTimeStringAMPM) || toTrimmedString(value?.localDateTimeString) || toTrimmedString(value?.localDateTime) || '';

const getLeadData = (row) => {
	const payload = isPlainObject(row?.data) ? row.data : {};
	const payloadData = isPlainObject(payload?.data) ? payload.data : {};
	const basePayload = getFirstNonEmptyObject([payloadData, payload]);

	return {
		...basePayload,
		leadCode: toTrimmedString(basePayload?.leadCode),
		leadSource: toTrimmedString(basePayload?.leadSource) || toTrimmedString(basePayload?.source),
		companyName: toTrimmedString(basePayload?.companyName),
		contactPerson: toTrimmedString(basePayload?.contactPerson),
		sourceIdentityProcessCode: toTrimmedString(basePayload?.sourceIdentityProcessCode),
		industry: toTrimmedString(basePayload?.industry),
		emailAddress: toTrimmedString(basePayload?.emailAddress),
		phoneNumber: toTrimmedString(basePayload?.phoneNumber),
		initialNotes: toTrimmedString(basePayload?.initialNotes),
		designation: toTrimmedString(basePayload?.designation),
		interestedSolution:
			toTrimmedString(basePayload?.['interestedSolution-displayName']) ||
			toTrimmedString(basePayload?.interestedSolutionDisplayName) ||
			toTrimmedString(basePayload?.interestedSolutionName) ||
			toTrimmedString(basePayload?.interestedSolution),
		priorityDisplayName: toTrimmedString(basePayload?.priorityDisplayName),
		priority: toTrimmedString(basePayload?.priority),
	};
};

const findCurrentTopLevelStage = (row) => {
	const stagePath = Array.isArray(row?.stagePath) ? row.stagePath : [];
	if (!stagePath.length) return null;

	const currentStageId = toTrimmedString(row?.currentStage?.stageId);
	const currentStageKey = toTrimmedString(row?.currentStage?.stageKey);

	const byId = stagePath.find((stage) => {
		const stageId = toTrimmedString(stage?.id ?? stage?._id);
		return stageId && stageId === currentStageId;
	});
	if (byId) return byId;

	return stagePath.find((stage) => {
		const stageKey = toTrimmedString(stage?.stageKey);
		const parentId = toTrimmedString(stage?.parentId);
		return stageKey === currentStageKey && !parentId;
	});
};

const hasNextTopLevelStage = (row) => {
	const stagePath = Array.isArray(row?.stagePath) ? row.stagePath : [];
	if (!stagePath.length) return true;

	const topLevelStages = stagePath.filter((stage) => !toTrimmedString(stage?.parentId));
	if (!topLevelStages.length) return true;

	const currentStageId = toTrimmedString(row?.currentStage?.stageId);
	const currentStageKey = toTrimmedString(row?.currentStage?.stageKey);
	const currentIndex = topLevelStages.findIndex((stage) => {
		const stageId = toTrimmedString(stage?.id ?? stage?._id);
		const stageKey = toTrimmedString(stage?.stageKey);
		return (currentStageId && stageId === currentStageId) || (!currentStageId && stageKey === currentStageKey) || stageKey === currentStageKey;
	});

	if (currentIndex < 0) return true;
	return currentIndex < topLevelStages.length - 1;
};

const buildStageTasksFromPath = (row) => {
	const currentTopStage = findCurrentTopLevelStage(row);
	const subStages = Array.isArray(currentTopStage?.subStages) ? currentTopStage.subStages : [];
	const parentStageId = toTrimmedString(currentTopStage?.id ?? currentTopStage?._id ?? currentTopStage?.stageId);
	return subStages.map((stage, index) => ({
		stageId: toTrimmedString(stage?.stageId ?? stage?.id ?? stage?._id),
		subStageId: toTrimmedString(stage?.stageId ?? stage?.id ?? stage?._id),
		parentId: toTrimmedString(stage?.parentId) || parentStageId,
		parentStageId: toTrimmedString(stage?.parentId) || parentStageId,
		taskKey: toTrimmedString(stage?.stageKey) || `${index + 1}`,
		taskName: toTrimmedString(stage?.stageName) || `Task ${index + 1}`,
		required: stage?.required === true,
		evidenceType: toTrimmedString(stage?.evidenceType),
	}));
};

const buildRequiredAttachmentsByTaskKey = (row) => {
	const currentTopStage = findCurrentTopLevelStage(row);
	const subStages = Array.isArray(currentTopStage?.subStages) ? currentTopStage.subStages : [];
	const attachmentMap = new Map();
	const stageLevelAttachments = Array.isArray(currentTopStage?.requiredAttachments)
		? currentTopStage.requiredAttachments
				.map((attachment) => {
					const title = toTrimmedString(attachment?.title);
					return title
						? {
								title,
								required: attachment?.required === true,
								attachmentKey: toTrimmedString(attachment?.attachmentKey),
								files: Array.isArray(attachment?.files) ? attachment.files : [],
							}
						: null;
				})
				.filter(Boolean)
		: [];

	subStages.forEach((stage) => {
		const taskKey = toTrimmedString(stage?.stageKey);
		if (!taskKey) return;
		const attachments = Array.isArray(stage?.requiredAttachments) ? stage.requiredAttachments : [];
		const normalized = attachments
			.map((attachment) => {
				const title = toTrimmedString(attachment?.title);
				return title
					? {
							title,
							required: attachment?.required === true,
							attachmentKey: toTrimmedString(attachment?.attachmentKey),
							files: Array.isArray(attachment?.files) ? attachment.files : [],
						}
					: null;
			})
			.filter(Boolean);
		attachmentMap.set(taskKey, normalized.length ? normalized : stageLevelAttachments);
	});

	return attachmentMap;
};

const buildStagePathChips = (row) => {
	const stagePath = Array.isArray(row?.stagePath) ? row.stagePath : [];
	const topLevelStages = stagePath.filter((stage) => !toTrimmedString(stage?.parentId));
	if (!topLevelStages.length) return [];

	const currentStageId = toTrimmedString(row?.currentStage?.stageId);
	const currentStageKey = toTrimmedString(row?.currentStage?.stageKey);
	const currentIndex = topLevelStages.findIndex((stage) => {
		const stageId = toTrimmedString(stage?.id ?? stage?._id);
		const stageKey = toTrimmedString(stage?.stageKey);
		return (currentStageId && stageId === currentStageId) || (!currentStageId && stageKey === currentStageKey) || stageKey === currentStageKey;
	});

	return topLevelStages.map((stage, index) => {
		const stageName = toTrimmedString(stage?.stageName) || toTrimmedString(stage?.stageKey) || `Stage ${index + 1}`;
		const status = currentIndex < 0 ? 'future' : index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'future';
		return {
			stageId: toTrimmedString(stage?.id ?? stage?._id),
			stageKey: toTrimmedString(stage?.stageKey),
			stageName,
			status,
		};
	});
};

const normalizeChecklistAttachments = (value) => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

	const normalized = {};
	Object.entries(value).forEach(([taskKey, attachments]) => {
		const safeTaskKey = toTrimmedString(taskKey);
		if (!safeTaskKey) return;

		const safeAttachments = Array.isArray(attachments)
			? attachments
					.map((attachment) => {
						if (!attachment || typeof attachment !== 'object') return null;
						const title = toTrimmedString(attachment?.title);
						const file = attachment?.file && typeof attachment.file === 'object' ? attachment.file : null;
						return {
							title,
							attachmentKey: toTrimmedString(attachment?.attachmentKey),
							fileIds: Array.isArray(attachment?.fileIds) ? attachment.fileIds : [],
							file,
						};
					})
					.filter(Boolean)
			: [];

		normalized[safeTaskKey] = safeAttachments;
	});

	return normalized;
};

const normalizeChecklistNotes = (value) => {
	if (!value) return {};

	if (Array.isArray(value)) {
		return value.reduce((accumulator, row) => {
			const taskKey = toTrimmedString(row?.checklistStageKey ?? row?.taskKey ?? row?.stageKey);
			if (!taskKey) return accumulator;
			accumulator[taskKey] = {
				noteText: toTrimmedString(row?.noteText ?? row?.note),
				updatedAt:
					toTrimmedString(row?.updatedAt?.localDateTimeStringAMPM) ||
					toTrimmedString(row?.updatedAt?.localDateTimeString) ||
					toTrimmedString(row?.updatedAt?.localDateTime) ||
					toTrimmedString(row?.updatedAt) ||
					'',
			};
			return accumulator;
		}, {});
	}

	if (typeof value !== 'object') return {};

	return Object.entries(value).reduce((accumulator, [key, entry]) => {
		const taskKey = toTrimmedString(key);
		if (!taskKey) return accumulator;

		if (typeof entry === 'string') {
			accumulator[taskKey] = { noteText: toTrimmedString(entry), updatedAt: '' };
			return accumulator;
		}

		accumulator[taskKey] = {
			noteText: toTrimmedString(entry?.noteText ?? entry?.note),
			updatedAt:
				toTrimmedString(entry?.updatedAt?.localDateTimeStringAMPM) ||
				toTrimmedString(entry?.updatedAt?.localDateTimeString) ||
				toTrimmedString(entry?.updatedAt?.localDateTime) ||
				toTrimmedString(entry?.updatedAt) ||
				'',
		};
		return accumulator;
	}, {});
};

const buildAttachmentKeyLookup = (row, checklistAttachments = {}) => {
	const lookup = new Map();
	const register = (rawKey, fileId, fileURL, fileName) => {
		const key = toTrimmedString(rawKey);
		if (!key) return;
		const id = toTrimmedString(fileId);
		const urlTail = toTrimmedString(fileURL).split('\\').pop();
		const name = toTrimmedString(fileName).toLowerCase();
		if (id) lookup.set(`id:${id}`, key);
		if (urlTail) lookup.set(`url:${urlTail.toLowerCase()}`, key);
		if (name) lookup.set(`name:${name}`, key);
	};

	const stageTasks = Array.isArray(row?.stageTasks) ? row.stageTasks : [];
	stageTasks.forEach((task) => {
		const requiredAttachments = Array.isArray(task?.requiredAttachments) ? task.requiredAttachments : [];
		requiredAttachments.forEach((requiredAttachment) => {
			const rawKey = toTrimmedString(requiredAttachment?.attachmentKey) || toTrimmedString(requiredAttachment?.title);
			const files = Array.isArray(requiredAttachment?.files) ? requiredAttachment.files : [];
			files.forEach((file) => register(rawKey, file?.fileId, file?.fileURL, file?.fileName));
		});
	});

	const stagePath = Array.isArray(row?.stagePath) ? row.stagePath : [];
	stagePath.forEach((stage) => {
		const subStages = Array.isArray(stage?.subStages) ? stage.subStages : [];
		subStages.forEach((subStage) => {
			const requiredAttachments = Array.isArray(subStage?.requiredAttachments) ? subStage.requiredAttachments : [];
			requiredAttachments.forEach((requiredAttachment) => {
				const rawKey = toTrimmedString(requiredAttachment?.attachmentKey) || toTrimmedString(requiredAttachment?.title);
				const files = Array.isArray(requiredAttachment?.files) ? requiredAttachment.files : [];
				files.forEach((file) => register(rawKey, file?.fileId, file?.fileURL, file?.fileName));
			});
		});
	});

	Object.values(checklistAttachments).forEach((rows) => {
		const safeRows = Array.isArray(rows) ? rows : [];
		safeRows.forEach((attachment) => {
			const rawKey = toTrimmedString(attachment?.attachmentKey) || toTrimmedString(attachment?.title);
			register(rawKey, '', attachment?.file?.url, attachment?.file?.name);
		});
	});

	return lookup;
};

const resolveAttachmentKey = (attachment, lookup) => {
	const explicit = toTrimmedString(attachment?.attachmentKey);
	if (explicit) return explicit;
	const id = toTrimmedString(attachment?.fileId);
	const urlTail = toTrimmedString(attachment?.fileURL).split('\\').pop();
	const name = toTrimmedString(attachment?.fileName).toLowerCase();
	return lookup.get(`id:${id}`) || lookup.get(`url:${urlTail.toLowerCase()}`) || lookup.get(`name:${name}`) || '--';
};

export const formatDuration = (value) => {
	if (!value) return '-';
	const enteredAt = dayjs(value);
	if (!enteredAt.isValid()) return '-';

	const minutes = dayjs().diff(enteredAt, 'minute');
	if (minutes < 1) return 'Just now';
	if (minutes < 60) return `${minutes}m ago`;

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ${minutes % 60}m ago`;

	const days = Math.floor(hours / 24);
	return `${days}d ${hours % 24}h ago`;
};

const getLeadTitle = (row) => {
	const data = getLeadData(row);
	return (
		toTrimmedString(data?.companyName) ||
		toTrimmedString(data?.contactPerson) ||
		toTrimmedString(data?.leadCode) ||
		'(Untitled lead)'
	);
};

const getLeadSubtitle = (row, { leadProductLabelByValue = {} } = {}) => {
	const data = getLeadData(row);
	const items = [];
	const interestedSolutionValue = toTrimmedString(data?.interestedSolution);
	const leadProductFromMap = toTrimmedString(leadProductLabelByValue?.[interestedSolutionValue]);
	const productDisplayName =
		leadProductFromMap ||
		toTrimmedString(data?.['interestedSolution-displayName']) ||
		toTrimmedString(data?.interestedSolutionDisplayName) ||
		toTrimmedString(data?.interestedSolutionName) ||
		toTrimmedString(data?.['leadProduct-displayName']) ||
		toTrimmedString(data?.leadProductDisplayName);

	if (toTrimmedString(data?.designation)) items.push(toTrimmedString(data.designation));
	if (productDisplayName) items.push(`Solution: ${productDisplayName}`);
	if (toTrimmedString(data?.contactPerson)) items.push(`Contact: ${toTrimmedString(data.contactPerson)}`);
	if (toTrimmedString(data?.leadSource)) items.push(`Source: ${toTrimmedString(data.leadSource)}`);
	if (toTrimmedString(row?.stageProfileName)) items.push(`Profile: ${toTrimmedString(row.stageProfileName)}`);
	return items.join(' | ');
};

export const getInitials = (name, fallback = '?') => {
	const safeName = toTrimmedString(name);
	if (!safeName) return fallback;

	const ignoreTokens = new Set(['mr', 'mrs', 'ms', 'miss', 'md', 'dr', 'prof']);
	const parts = safeName
		.split(' ')
		.map((part) => part.trim())
		.filter(Boolean)
		.filter((part) => !ignoreTokens.has(part.toLowerCase().replace(/\./g, '')));
	if (!parts.length) return fallback;

	return parts
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');
};

const parseSupportUserIds = (value) => {
	if (Array.isArray(value)) {
		return value.map((item) => toTrimmedString(item)).filter(Boolean);
	}

	const safeValue = toTrimmedString(value);
	if (!safeValue) return [];

	return safeValue
		.split(',')
		.map((item) => toTrimmedString(item))
		.filter(Boolean);
};

export const toUserOptions = (results) => {
	const rows = Array.isArray(results) ? results : [];
	const options = rows
		.map((row) => {
			const label = toTrimmedString(row?.displayName ?? row?.userFullName ?? row?.propertyValue ?? row?.propertyCode);
			const value = toTrimmedString(row?.userId ?? row?.propertyValue ?? row?.propertyCode);
			if (!label || !value) return null;
			return { label, value };
		})
		.filter(Boolean);

	const unique = new Map();
	options.forEach((option) => unique.set(option.value, option));
	return Array.from(unique.values());
};

const computeSlaProgress = ({ startAt, dueAt, fallbackHours = 24 }) => {
	const start = dayjs(startAt);
	const due = dayjs(dueAt);
	if (!start.isValid() || !due.isValid() || !due.isAfter(start)) {
		const elapsedHours = start.isValid() ? dayjs().diff(start, 'hour', true) : 0;
		const fallbackProgress = Math.max(0, Math.min(100, Math.round((elapsedHours / fallbackHours) * 100)));
		return {
			progress: fallbackProgress,
			isBreached: elapsedHours > fallbackHours,
		};
	}

	const totalMs = due.diff(start, 'millisecond');
	const elapsedMs = dayjs().diff(start, 'millisecond');
	const progress = Math.max(0, Math.min(100, Math.round((elapsedMs / totalMs) * 100)));
	return {
		progress,
		isBreached: dayjs().isAfter(due),
	};
};

export const getEventTimestamp = (event) =>
	toTrimmedString(event?.at?.localDateTimeStringAMPM) ||
	toTrimmedString(event?.at?.localDateTimeString) ||
	toTrimmedString(event?.at?.localDateTime) ||
	'-';

const toSafeJson = (value) => {
	try {
		return JSON.stringify(value);
	} catch (error) {
		return '';
	}
};

const normalizeReadableToken = (value) => {
	const safe = toTrimmedString(value);
	if (!safe) return '';
	return safe
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase()
		.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const formatEventDetails = (event) => {
	const type = toTrimmedString(event?.type).toUpperCase();
	const data = event?.data && typeof event.data === 'object' ? event.data : {};

	if (type === 'STAGE_CHANGED') {
		const from = normalizeReadableToken(data?.from) || '-';
		const to = normalizeReadableToken(data?.to) || '-';
		return `Stage moved from ${from} to ${to}.`;
	}

	if (type === 'TASK_DONE' || type === 'CHECKLIST_UPDATED') {
		const taskKey = normalizeReadableToken(data?.taskKey || data?.checklistStageKey) || 'this checklist item';
		const doneValue = Number(data?.done);
		const totalValue = Number(data?.total);
		const done = Number.isFinite(doneValue) ? doneValue : '-';
		const total = Number.isFinite(totalValue) ? totalValue : '-';
		const completed = toTrimmedString(data?.completed).toLowerCase() === 'true' || data?.completed === true;
		const action = completed ? 'completed' : 'updated';
		if (done !== '-' && total !== '-') {
			return `Checklist item ${taskKey} ${action}. Progress is ${done} out of ${total}.`;
		}
		return `Checklist item ${taskKey} ${action}.`;
	}

	if (type === 'ADD_CHECKLIST_STAGE') {
		const stageName = normalizeReadableToken(data?.checklistStageKey || data?.stageKey) || 'a checklist item';
		return `${stageName} was added to the current stage checklist.`;
	}

	if (type === 'ASSIGNED') {
		const owner = toTrimmedString(data?.ownerFullName) || toTrimmedString(data?.ownerUserId);
		const added = Array.isArray(data?.supportUsersAdded) ? data.supportUsersAdded.length : 0;
		const removed = Array.isArray(data?.supportUsersRemoved) ? data.supportUsersRemoved.length : 0;
		if (owner) return `Owner assigned to ${owner}.`;
		if (added) return `${added} support user${added > 1 ? 's were' : ' was'} added.`;
		if (removed) return `${removed} support user${removed > 1 ? 's were' : ' was'} removed.`;
	}

	if (type === 'DATA_UPDATED') {
		const keys = Array.isArray(data?.keys) ? data.keys.filter(Boolean) : [];
		if (keys.length) return `Details were updated (${keys.map((key) => normalizeReadableToken(key)).join(', ')}).`;
	}

	const compactJson = toSafeJson(data);
	if (compactJson) return 'Activity was recorded successfully.';
	return '-';
};

export const buildRoomId = ({ processCode, itemId }) => `${toTrimmedString(processCode)}_${toTrimmedString(itemId)}`;

export const getChatTime = (message) =>
	toTrimmedString(message?.time?.localDateTimeStringAMPM) ||
	toTrimmedString(message?.time?.localDateTimeString) ||
	toTrimmedString(message?.time?.localDateTime) ||
	'-';

export const toWorkBenchCards = (responseData, { leadProductLabelByValue = {} } = {}) => {
	const rows = Array.isArray(responseData) ? responseData : [];
	return rows
		.map((row) => {
			const id = getResourceId(row);
			if (!id) return null;

			const enteredAt = getEnteredAt(row);
			const createdAt = getCreatedAt(row);
			const stageDueAt = getSlaDate(row?.sla?.dueAt);
			const globalDueAt = getSlaDate(row?.sla?.globalDueAt);
			const stageSla = computeSlaProgress({ startAt: enteredAt, dueAt: stageDueAt, fallbackHours: 24 });
			const totalSla = computeSlaProgress({ startAt: createdAt, dueAt: globalDueAt, fallbackHours: 48 });
			const isBreach = Boolean(row?.sla?.breached) || stageSla.isBreached;
			const stageTasks = Array.isArray(row?.stageTasks) && row.stageTasks.length ? row.stageTasks : buildStageTasksFromPath(row);
			const completedTaskKeys = Array.isArray(row?.completedTaskKeys) ? row.completedTaskKeys.map((key) => toTrimmedString(key)) : [];
			const supportTeam = Array.isArray(row?.supportTeam) ? row.supportTeam : [];
			const supportMembersFromTeam = supportTeam
				.map((support) => {
					const userId = toTrimmedString(support?.entityCode ?? support?.userId);
					if (!userId) return null;
					const fullName = toTrimmedString(support?.entityFullName ?? support?.displayName ?? support?.userFullName) || userId;
					const initials = getInitials(fullName, userId.substring(0, 2).toUpperCase());
					return {
						userId,
						fullName,
						initials,
					};
				})
				.filter(Boolean);
			const supportUserNames = Array.isArray(row?.supportUserNames) ? row.supportUserNames : [];
			const supportMembersFromNames = supportUserNames
				.map((fullName, index) => {
					const safeName = toTrimmedString(fullName);
					const userId = toTrimmedString(row?.supportUserIds?.[index] ?? safeName);
					if (!safeName && !userId) return null;
					return {
						userId: userId || safeName,
						fullName: safeName || userId,
						initials: getInitials(safeName || userId, getInitials(userId, 'U')),
					};
				})
				.filter(Boolean);
			const supportByUserId = new Map([...supportMembersFromTeam, ...supportMembersFromNames].map((support) => [support.userId, support]));
			parseSupportUserIds(row?.supportUserIds).forEach((supportUserId) => {
				if (!supportByUserId.has(supportUserId)) {
					supportByUserId.set(supportUserId, {
						userId: supportUserId,
						fullName: supportUserId,
						initials: getInitials(supportUserId, supportUserId.substring(0, 2).toUpperCase()),
					});
				}
			});
			const supportMembers = Array.from(supportByUserId.values());
			const data = getLeadData(row);
			const requiredAttachmentsByTaskKey = buildRequiredAttachmentsByTaskKey(row);
			const checklistAttachments = normalizeChecklistAttachments(
				data?.checklistAttachments ?? row?.checklistAttachments ?? row?.data?.checklistAttachments
			);
			const checklistNotes = normalizeChecklistNotes(
				data?.checklistNotes ?? row?.checklistNotes ?? row?.data?.checklistNotes ?? data?.notes ?? row?.notes
			);
			const attachmentKeyLookup = buildAttachmentKeyLookup(row, checklistAttachments);
			const currentTopStage = findCurrentTopLevelStage(row);
			const currentTopStageKey = toTrimmedString(currentTopStage?.stageKey);
			const currentTopStageId = toTrimmedString(currentTopStage?.id ?? currentTopStage?._id ?? currentTopStage?.stageId);
			const currentTopStageSubStages = Array.isArray(currentTopStage?.subStages) ? currentTopStage.subStages : [];
			const normalizedTasks = stageTasks.map((task, index) => {
				const matchedSubStage = currentTopStageSubStages.find((subStage) => {
					const subStageId = toTrimmedString(subStage?.stageId ?? subStage?.id ?? subStage?._id);
					const taskStageId = toTrimmedString(task?.stageId ?? task?.id ?? task?._id);
					const subStageKey = toTrimmedString(subStage?.stageKey);
					const taskKey = toTrimmedString(task?.taskKey ?? task?.stageKey);
					return (taskStageId && subStageId === taskStageId) || (taskKey && subStageKey === taskKey);
				});

				return {
					stageId:
						toTrimmedString(task?.stageId) ||
						toTrimmedString(task?.id) ||
						toTrimmedString(matchedSubStage?.stageId ?? matchedSubStage?.id ?? matchedSubStage?._id) ||
						'',
					subStageId:
						toTrimmedString(task?.subStageId) ||
						toTrimmedString(task?.stageId) ||
						toTrimmedString(task?.id) ||
						toTrimmedString(matchedSubStage?.stageId ?? matchedSubStage?.id ?? matchedSubStage?._id) ||
						'',
					parentId:
						toTrimmedString(task?.parentId) ||
						toTrimmedString(task?.parentStageId) ||
						toTrimmedString(matchedSubStage?.parentId) ||
						currentTopStageId,
					parentStageId:
						toTrimmedString(task?.parentStageId) ||
						toTrimmedString(task?.parentId) ||
						toTrimmedString(matchedSubStage?.parentId) ||
						currentTopStageId,
					taskKey: toTrimmedString(task?.taskKey) || `${index + 1}`,
					taskName: toTrimmedString(task?.taskName) || `Task ${index + 1}`,
					required: Boolean(task?.required),
					isPicked: Boolean(task?.isPicked ?? task?.required),
					done: Boolean(task?.checked) || completedTaskKeys.includes(toTrimmedString(task?.taskKey)),
					evidenceType: toTrimmedString(task?.evidenceType),
					completionDateTime: getTaskDateTime(task?.completionDateTime) || getTaskDateTime(task?.completedAt) || null,
					externalProcessCodes: Array.isArray(task?.externalProcessCodes) ? task.externalProcessCodes : [],
					externalProcesses: Array.isArray(task?.externalProcesses)
						? task.externalProcesses
						: task?.externalProcesses && typeof task.externalProcesses === 'object'
							? [task.externalProcesses]
							: [],
					requiredAttachments: requiredAttachmentsByTaskKey.get(toTrimmedString(task?.taskKey)) ?? [],
					uploadedAttachments: checklistAttachments[toTrimmedString(task?.taskKey)] ?? checklistAttachments[currentTopStageKey] ?? [],
					noteText: checklistNotes[toTrimmedString(task?.taskKey)]?.noteText ?? toTrimmedString(task?.noteText) ?? '',
					noteUpdatedAt: checklistNotes[toTrimmedString(task?.taskKey)]?.updatedAt ?? getTaskDateTime(task?.noteUpdatedAt) ?? '',
				};
			});
			const requiredTasks = normalizedTasks.filter((task) => task.required);
			const allRequiredTasksDone = requiredTasks.length ? requiredTasks.every((task) => task.done) : true;

			return {
				_id: toTrimmedString(row?._id),
				processCode: toTrimmedString(row?.processCode),
				id,
				title: getLeadTitle(row),
				subtitle: getLeadSubtitle(row, { leadProductLabelByValue }),
				scmPriority: toTrimmedString(data?.priority) || toTrimmedString(data?.priorityDisplayName) || toTrimmedString(data?.priorityLevel),
				leadCode:
					toTrimmedString(data?.leadCode) ||
					toTrimmedString(data?.rawReferenceId),
				leadSource:
					toTrimmedString(data?.leadSource),
				leadCompanyName: toTrimmedString(data?.companyName),
				leadContactPerson: toTrimmedString(data?.contactPerson),
				leadIndustry: toTrimmedString(data?.industry),
				leadDesignation: toTrimmedString(data?.designation),
				leadInterestedSolution:
					toTrimmedString(leadProductLabelByValue?.[toTrimmedString(data?.interestedSolution)]) ||
					toTrimmedString(data?.interestedSolution),
				phoneNumber: toTrimmedString(data?.phoneNumber),
				emailAddress: toTrimmedString(data?.emailAddress),
				initialNotes: toTrimmedString(data?.initialNotes),
				sourceIdentityProcessCode: toTrimmedString(data?.sourceIdentityProcessCode),
				stageKey: getStageKey(row) || 'UNKNOWN',
				stageName: getStageName(row),
				waitTime: formatDuration(enteredAt),
				owner: toTrimmedString(row?.ownerFullName) || toTrimmedString(row?.ownerUserName) || toTrimmedString(row?.ownerUserId) || '-',
				ownerUserId: toTrimmedString(row?.ownerUserId),
				ownerImage:
					toTrimmedString(row?.ownerImage) ||
					toTrimmedString(row?.ownerUserImage) ||
					toTrimmedString(row?.ownerAvatar) ||
					toTrimmedString(row?.ownerPhotoUrl) ||
					'',
				ownerInitials: getInitials(
					toTrimmedString(row?.ownerFullName) || toTrimmedString(row?.ownerUserName),
					getInitials(toTrimmedString(row?.ownerUserId), 'U')
				),
				supportMembers,
				supportCount: supportMembers.length,
				priority:
					toTrimmedString(data?.priority) || toTrimmedString(data?.priorityDisplayName) || toTrimmedString(data?.priorityLevel) || '-',
				stageProgress: stageSla.progress,
				totalProgress: totalSla.progress,
				isBreach,
				stageTasks: normalizedTasks,
				stagePath: Array.isArray(row?.stagePath) ? row.stagePath : [],
				currentStageId: toTrimmedString(row?.currentStage?.stageId),
				stagePathChips: buildStagePathChips(row),
				parallelTasks: Array.isArray(data?.additionalParallelProcesses) ? data.additionalParallelProcesses : [],
				references: Array.isArray(row?.references)
					? row.references
							.map((reference) => ({
								stageId: toTrimmedString(reference?.stageId),
								processCode: toTrimmedString(reference?.processCode),
								processName: toTrimmedString(reference?.processName),
								processVersion: toTrimmedString(reference?.processVersion),
								staticFormUrl: toTrimmedString(reference?.staticFormUrl),
								verticalCode: toTrimmedString(reference?.verticalCode),
								verticalName: toTrimmedString(reference?.verticalName),
								entryId: toTrimmedString(reference?.entryId),
								activationStatus: toTrimmedString(reference?.activationStatus),
								dateTime: toTrimmedString(reference?.dateTime),
								creatorUserId: toTrimmedString(reference?.creatorUserId),
							}))
							.filter((reference) => reference.processName)
					: [],
				attachments: Array.isArray(row?.attachments)
					? row.attachments
							.map((attachment) => ({
								stageTitle: attachment?.stageTitle || '-',
								subStageTitle: attachment?.subStageTitle || '-',
								fileId: toTrimmedString(attachment?.fileId),
								fileName: toTrimmedString(attachment?.fileName),
								fileURL: toTrimmedString(attachment?.fileURL),
								attachmentKey: resolveAttachmentKey(attachment, attachmentKeyLookup),
								fileSize: attachment?.fileSize ?? null,
								uploadedAt: attachment?.uploadedAt ?? null,
							}))
							.filter((attachment) => attachment.fileId || attachment.fileName || attachment.fileURL)
					: [],
				checklistAttachments,
				checklistNotes,
				allRequiredTasksDone,
				hasNextStage: hasNextTopLevelStage(row),
			};
		})
		.filter(Boolean);
};

export const toWorkBenchCardBuckets = (responseData, { leadProductLabelByValue = {} } = {}) => {
	return {
		inbound: toWorkBenchCards(responseData?.inbound, { leadProductLabelByValue }),
		outbound: toWorkBenchCards(responseData?.outbound, { leadProductLabelByValue }),
		collab: toWorkBenchCards(responseData?.collab, { leadProductLabelByValue }),
	};
};
